import { type Skill } from 'src/copilot/types/skills'
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils'
import {
  FLOW_DATASOURCE_ENTITY_TYPE_TO_PLATFORM,
  type FlowDatasourceEntityType
} from 'shared/copilot/flowDataAgentConstants'

/** Platforms referenced by a skill's configured data sources (unique). */
export const getRequiredPlatformsForSkill = (skill: Skill): string[] => {
  return [...new Set(skill.dataSources.map((dataSource) => dataSource.platform))]
}

export const getSelectedPlatformsFromEntityTypes = (
  entityTypes: readonly FlowDatasourceEntityType[]
): ReadonlySet<string> => {
  return new Set(entityTypes.map((entityType) => FLOW_DATASOURCE_ENTITY_TYPE_TO_PLATFORM[entityType]))
}

export const getPlatformDisplayName = (platform: string): string => {
  return getPlatformDetailsByPlatform(platform)?.label ?? platform
}

/**
 * A skill is selectable when it is active and either has no required platforms,
 * or every required platform is present in the currently selected datasources.
 */
export const isSkillSelectable = (skill: Skill, selectedPlatforms: ReadonlySet<string>): boolean => {
  if (skill.status !== 'active') {
    return false
  }
  const requiredPlatforms = getRequiredPlatformsForSkill(skill)
  if (requiredPlatforms.length === 0) {
    return true
  }
  return requiredPlatforms.every((platform) => selectedPlatforms.has(platform))
}

export const getMissingPlatformsForSkill = (skill: Skill, selectedPlatforms: ReadonlySet<string>): string[] => {
  return getRequiredPlatformsForSkill(skill).filter((platform) => !selectedPlatforms.has(platform))
}

export const filterSkillsByQuery = (skills: readonly Skill[], query: string): Skill[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (normalizedQuery.length === 0) {
    return [...skills]
  }
  return skills.filter((skill) => {
    const haystack = `${skill.name} ${skill.flowName ?? ''} ${skill.description}`.toLowerCase()
    return haystack.includes(normalizedQuery)
  })
}

/** Org-wide skills are Precis-only: users without internal feature access never see them. */
export const filterSkillsByInternalAccess = (skills: readonly Skill[], hasInternalFeatureAccess: boolean): Skill[] =>
  hasInternalFeatureAccess ? [...skills] : skills.filter((skill) => skill.scope !== 'organisation')

/** Active skills only — paused skills stay in Settings but not in the composer picker. */
export const getActiveSkillsForComposer = (skills: readonly Skill[]): Skill[] => {
  return skills.filter((skill) => skill.status === 'active')
}

/** True when the composer `/` menu has at least one skill to offer. */
export const hasComposerSkills = (skills: readonly Skill[]): boolean =>
  skills.some((skill) => skill.status === 'active')
